/* -------- packages -------- */
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useRef, useEffect, useCallback } from "react"; 
import { Button, Card, CardBody, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, UncontrolledTooltip } from "reactstrap";
import { ChevronDown, X } from "lucide-react";
import html2canvas from 'html2canvas';
/* -------- ------- -------- */

/* -------- ------- -------- */
import ficheTechnique from '../../assets/fakedata/ficheTechnique.pdf';
import { checkConfigurationFailure, checkConfigurationRequest, fetchConfigurationRequest, verifyConfigurationRequest, fetchConfigurationsRequest, fetchNotificationsRequest, fetchProductsRequest, updateNotificationRequest, uploadDocumentRequest, clearVerifyConfigurationErrors, clearVerifyConfigurationSuccess, clearDocumentSuccess, clearConfigurationErrors } from "../../store/actions";
import { useParams } from "react-router-dom";
import { fetchLinesRequest } from "../../store/lines/actions";
import { HOST_API_KEY } from "../../config-global";
import { transparentImage } from "../../utils/items";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { clearQuoteSuccess, createQuoteRequest, quoteToPdfRequest } from "../../store/quotes/actions";
import { get } from "../../helpers/api_helper";
import RenderValidationModal from "../../components/ui/Modals/RenderValidationModal";
import { dictionary } from "../../utils/dictionary";
import IntensityModal from "../../components/ui/Modals/IntensityModal";

export default function ConfigurationEditPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id: configId } = useParams();
  const parameters = useSelector(state => state.Parameters);
  const products = useSelector(state => state.Products.products);
  const config = useSelector(state => state.Configurations.configuration);
  const { configurations, checkConfiguration } = useSelector(state => state.Configurations);
  const { verifiedConfiguration, verifyConfigurationSuccess, loadingVerifyConfig, errorVerifyConfig } = useSelector(state => state.Configurations);
  const documents = useSelector(state => state.Documents);
  const notifications = useSelector(state => state.Notifications.notifications);
  const user = useSelector(state => state.Login.user);
  const { loadingDoc } = useSelector((state) => state.Documents);
  const { loadingCreateQuote, loadingQuoteToPdf, lastCreatedQuote } = useSelector((state) => state.Quotes);

  const [quoteBlob, setQuoteBlob] = useState(null);
  // const lines = useSelector(state => state.Lines.lines); // lines from store, not directly used

  
  document.title = `${t('editConfig')} | ${parameters.appName}`; 

  
  const [centralStack, setCentralStack] = useState([]);
  const [cachedCentralStack, setCachedCentralStack] = useState([]);
  const [cachedElementSlots, setCachedElementSlots] = useState([]);
  const [elementSlots, setElementSlots] = useState({});
  const [isShifting, setIsShifting] = useState(false);
  const [dropInfo, setDropInfo] = useState({ itemIndex: null, position: null, slotInfo: null });
  const [modifiedSlot, setModifiedSlot] = useState(new Set());
  const [errorSlots, setErrorSlots] = useState({});
  const [draggingItem, setDraggingItem] = useState(null);
  
  const dragItem = useRef(null);
  const dragSourceType = useRef(null);
  const currentHoveredRef = useRef(null);
  const centralContainerRef = useRef(null);
  const slotsContainerRef = useRef(null);
  
  const [isDraggingOverContainer, setIsDraggingOverContainer] = useState(false);
  const [draggingOverSlot, setDraggingOverSlot] = useState(null);
  const [dragErrorMessage, setDragErrorMessage] = useState(null);
  const [elementContainerVisible, setElementContainerVisible] = useState(false);

  const [configToSend, setConfigToSend] = useState(null);
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [isValidationInProgress, setIsValidationInProgress] = useState(false);

  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const [isIntensityModalOpen, setIsIntensityModalOpen] = useState(false);

  const [allProducts, setAllProducts] = useState([]);
  const [leftSidebarItems, setLeftSidebarItems] = useState([]);
  const [cachesWithImages, setCachesWithImages] = useState([]);
  const [allCaches, setAllCaches] = useState([]);
  const dictCaches = {
    '350826' : '350895',
    '350816' : '350894',
    '350817' : '350895',
    '350814' : '350825',
  };
  const [configurationImage, setConfigurationImage] = useState(null);

  const [allErrors, setAllErrors] = useState([]);

  const isDataLoadedRef = useRef(false);

  const [quotingProcessStarted, setQuotingProcessStarted] = useState(false);

  const isConfigurationValidated = () => {
    return config?.status === dictionary.projQuotes.statusDone || 
          config?.status === dictionary.projQuotes.statusQuoteOK || 
          config?.quoteUrl;
  };

  useEffect(() => {
    // Nettoyer les états liés aux devis au montage du composant
    dispatch(clearDocumentSuccess());
    dispatch(clearQuoteSuccess());
    dispatch(clearVerifyConfigurationSuccess());
    dispatch(clearVerifyConfigurationErrors());
    
    // Autres dispatches existants
    dispatch(fetchProductsRequest());
    dispatch(fetchNotificationsRequest())
    if(configId){
      dispatch(fetchConfigurationRequest(configId));
    }
    dispatch(fetchLinesRequest()); 
  }, [dispatch, configId]);
  
  useEffect( () => {
    if (configId) {
      dispatch(fetchConfigurationRequest(configId));
    }
  }, [showConfirmationPopup])

  useEffect(() => {
    if (products?.length) {
      const filtered = products.filter(product => product.isDraggable);
      const filteredCaches = products.filter(product => product.categoryLib === 'Caches et Adaptateurs');
      setAllProducts(filtered);
      setAllCaches(filteredCaches);
    }
  }, [products]);

  useEffect(() => {
    if (allProducts?.length) {
      const productsWithImages = allProducts.map(product => {
        if (product && product.urlImage) {
          const imageUrl = HOST_API_KEY + product.urlImage;
          return { ...product, img: imageUrl };
        }
        return { ...product };
      });
      setLeftSidebarItems(productsWithImages);
    }
    if (allCaches?.length) {
      const cachesWithImages = allCaches.map(cache => {
        if (cache && cache.urlImage) {
          const imageUrl = HOST_API_KEY + cache.urlImage;
          return { ...cache, img: imageUrl };
        }
        return { ...cache };
      });
      setCachesWithImages(cachesWithImages);
    }

  }, [allProducts, allCaches]);

  const groupedProducts = leftSidebarItems.reduce((acc, product) => {
    if (product.isDraggable) {
      const category = product.categoryLib || 'Autres';
      if (!acc[category]) acc[category] = [];
      acc[category].push(product);
    }
    return acc;
  }, {});

  const { errorCheckConfig, loadingCheckConfig } = useSelector(state => state.Configurations); 
  // Refs to store previous stringified state for comparison in the API calling useEffect

  const prevElementSlotsStrRef = useRef(JSON.stringify({}));
  const prevElementSlotsJson = JSON.parse(prevElementSlotsStrRef.current);
  const prevCentralStackStrRef = useRef(JSON.stringify([]));

  // handleCheckBoxRules: Only dispatches the request.
  const handleCheckBoxRules = useCallback((configWithSlots) => {
    if (configWithSlots) {
      dispatch(checkConfigurationRequest(configWithSlots));
    }
  }, [dispatch]); // Dependency is just dispatch

  const handleCheckErrors = () => {

    const slotsInConfig = Object.keys(prevElementSlotsJson) // Get all slots from the previous elementSlots JSON
    const errorKeySlots = Object.keys(errorSlots); // Get all error slots from the current errorSlots state
    
    const errorSlotsNotInConfig = [...errorKeySlots].filter(slot => !slotsInConfig.includes(slot)); // Filter error slots that are not in the current configuration

    if (errorSlotsNotInConfig) {
      setErrorSlots(
        prev => {
          const newErrorSlots = { ...prev }; // Create a shallow copy of the previous errorSlots
          errorSlotsNotInConfig.forEach(slot => {
            delete newErrorSlots[slot]; // Remove only the slots that are not in the current configuration
          });
          return newErrorSlots; // Return the updated errorSlots state
        }
      )
    }

  }

  const buildAndCheckConfigurationWithSlots = useCallback((currentCentralStack, currentElementSlots) => {
    if (!config || !config.lib) {
      return;
    }

    const linesWithSlots = currentCentralStack.map((line) => {
      const lineStackId = line.stackId; 
      const currentLineSlots = [];
      for (const key in currentElementSlots) {
        if (key.startsWith(`${lineStackId}-`)) {
          currentLineSlots.push(currentElementSlots[key]);
        }
      }
      currentLineSlots.sort((a, b) => a.slot - b.slot);
      
      return {
        ...line,
        ProjProduct: line.ProjProduct,
        ProjSlots: currentLineSlots.map(slotData => ({
          ...slotData,
          ProjProduct: { ...slotData }
        })), 
      };
    });

    const objToSend = {
      ...config,
      ProjLines: linesWithSlots,
      isCircuitBreakerAdded: null
    };
    
    setConfigToSend(objToSend);
    handleCheckBoxRules(objToSend);
  }, [config, handleCheckBoxRules]);

  // handle error toasting
  useEffect(() => {
    handleCheckErrors();
  
    const intensityError = (errorCheckConfig?.data?.errors?.[0]?.code == "INTENSITY_LIMIT_EXCEEDED");
  
    if (intensityError && !loadingCheckConfig) {
      setIsIntensityModalOpen(true);
    }
  
    if (errorVerifyConfig && !loadingVerifyConfig) {
      const message = errorVerifyConfig?.data?.errors?.[0]?.originalErr || t('errorVerifyingConfiguration');
      toast.warn(message);
      dispatch(clearVerifyConfigurationErrors(null));
    }
  
    if (errorCheckConfig && !loadingCheckConfig) {
      const errors = errorCheckConfig?.data?.errors || [];
      
      // 1. TOASTER TOUTES LES ERREURS
      errors.forEach(err => {
        toast.error(t(err.code) || t('errorCheckingConfiguration'));
      });
  
      // 2. GÉRER LES ERREURS DE SLOTS SPÉCIFIQUEMENT
      const slotErrors = errors.filter(err => err.slotInfo);
      const newErrorSlots = {};
      
      slotErrors.forEach(error => {
        const { lineId, slotIndex } = error.slotInfo;
        const slotKey = `${lineId}-${slotIndex}`;
        newErrorSlots[slotKey] = error.originalErr;
      });
      
      setErrorSlots(newErrorSlots);
  
      // 3. STOCKER TOUTES LES ERREURS POUR LE BOUTON VALIDER
      setAllErrors(errors.filter((error) => error.code!="INSUFFICIENT_SPACE" && error.code!="MODULE_INTERVAL_CHANGE").map(err => err.originalErr || t(err.code) || t('errorCheckingConfiguration')));
      dispatch(clearConfigurationErrors());
    }
  }, [errorCheckConfig, loadingCheckConfig, errorVerifyConfig, loadingVerifyConfig]);

  const isValidateButtonDisabled = () => {
    return (
      centralStack.length === 0 ||
      Object.keys(errorSlots).length > 0 ||
      allErrors.length > 0 ||
      !user.isVerified ||
      (config?.status == dictionary.projQuotes.statusDone ||
      config?.status === dictionary.projQuotes.statusQuoteOK || 
      config?.quoteUrl ||
      config?.ProjProject?.status === dictionary.projProject.statusFinished)
    );
  };
  
  const getValidateButtonTooltipMessage = () => {
    const messages = [];
    
    if (centralStack.length === 0) {
      messages.push(t('noLinesInStack'));
    }
    
    if (Object.keys(errorSlots).length > 0) {
      messages.push(t('slotsError'));
      // Optionnel: ajouter le détail des erreurs de slots
      Object.values(errorSlots).forEach(error => {
        messages.push(`• ${error}`);
      });
    }
    
    if (allErrors.length > 0) {
      messages.push(t('configurationErrors'));
      allErrors.forEach(error => {
        messages.push(`• ${error}`);
      });
    }
    
    if (!user.isVerified) {
      messages.push(t('userNotVerified'));
    }
    
    if (config?.status === dictionary.projQuotes.statusDone || 
        config?.status === dictionary.projQuotes.statusQuoteOK || 
        config?.quoteUrl) {
      messages.push(t('configurationCompleted'));
    }
    
    if (config?.ProjProject?.status === dictionary.projProject.statusFinished) {
      messages.push(t('projectFinished'));
    }
    
    return messages.join('\n');
  };
  
  const isInitializedRef = useRef(false);

  // useEffect to populate centralStack and elementSlots from config
  const processConfigData = (
  config,
  configId,
  isInitializedRef,
  prevElementSlotsStrRef,
  prevCentralStackStrRef,
  isVerification = false
) => {
  let result;
  if (configId && config && config.ProjLines) {
    const newLinesForCentralStack = config.ProjLines.map((projLine, index) => {
      const lineProductId = projLine.ProjProduct?.id || `unknownProd${index}`;
      const stackId = projLine.id ? `line-${projLine.id}` : `line-idx-${index}-${lineProductId}`;
      return {
        ...projLine,
        id: projLine.id,
        lib: projLine.ProjProduct?.lib || projLine.lib || 'Unknown Line',
        stackId: stackId,
        slots: projLine.ProjProduct?.slots ?? 0,
        img: projLine.ProjProduct?.urlImage ? HOST_API_KEY + projLine.ProjProduct.urlImage : transparentImage,
          ProjProduct: projLine.ProjProduct 
      };
    }).sort((a, b) => (a.line || 0) - (b.line || 0));

    let newElementSlotsFromConfig = {};
    newLinesForCentralStack.forEach((lineInCentralStack) => {
      if (lineInCentralStack.ProjSlots && Array.isArray(lineInCentralStack.ProjSlots)) {
        lineInCentralStack.ProjSlots.forEach(slotInfo => {
          if (slotInfo.ProjProduct && typeof slotInfo.slot === 'number') {
              // Convertir le slot en nombre pour gérer les floats
              const numericSlot = parseFloat(slotInfo.slot);
              const slotKey = `${lineInCentralStack.stackId}-${numericSlot}`;
            
            newElementSlotsFromConfig[slotKey] = {
              ...slotInfo.ProjProduct,
              id: slotInfo.ProjProduct.id,
              lib: slotInfo.ProjProduct.lib || 'Unknown Element',
                slotId: `${numericSlot}-${slotInfo.ProjProduct.id}`,
                slot: numericSlot,
              img: slotInfo.ProjProduct.urlImage ? HOST_API_KEY + slotInfo.ProjProduct.urlImage : transparentImage,
            };
          }
        });
      }
    });

    if (!isInitializedRef.current) {
      prevElementSlotsStrRef.current = JSON.stringify(newElementSlotsFromConfig);
      prevCentralStackStrRef.current = JSON.stringify(newLinesForCentralStack);
    }

    result = {
      centralStack: newLinesForCentralStack,
      elementSlots: newElementSlotsFromConfig
    };
  } else if (!config) {
    prevElementSlotsStrRef.current = JSON.stringify({});
    prevCentralStackStrRef.current = JSON.stringify([]);
    isInitializedRef.current = false;

    result = {
      centralStack: [],
      elementSlots: {}
    };
  }

  if (result) {
    setCentralStack(result.centralStack);
    setElementSlots(result.elementSlots);
    if (isVerification) {
      setIsValidationInProgress(true);
      setShowValidationPopup(true)
    }
  }


  return result;
};


  useEffect(() => {
    if (config) {
      processConfigData(
        config,
        configId,
        isInitializedRef,
        prevElementSlotsStrRef,
        prevCentralStackStrRef,
        false
      );
    }
  
  }, [configId, config]);

  useEffect(() => {
    if (checkConfiguration) {
      processConfigData(
        checkConfiguration?.configuration,
        configId,
        isInitializedRef,
        prevElementSlotsStrRef,
        prevCentralStackStrRef,
        false
      );
    }
  }, [configId, checkConfiguration]);

  const buildAndCheckConfiguration = useCallback(() => {
    buildAndCheckConfigurationWithSlots(centralStack, elementSlots);
  }, [centralStack, elementSlots, buildAndCheckConfigurationWithSlots]);


  const createConfigurationImage = async () => {
    // Sélectionner l'élément contenant la configuration
    const configElement = document.querySelector('.popup-stack-items');

    if (!configElement) {
      return null; // Return null if the element is not found
    }
        
    try {
      const options = {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        foreignObjectRendering: false,
        imageTimeout: 0,
        removeContainer: true
      };

      // Load all images first to ensure they're ready for canvas
      const images = Array.from(configElement.getElementsByTagName('img'));
      await Promise.all(images.map(img => {
        if (img.complete) {
          return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
      }));

      
      const canvas = await html2canvas(configElement, options);
      
      // Convertir le canvas en Blob
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png', 1.0);
      });

      
      const fileName = `configuration_${new Date().toISOString().slice(0,10)}.png`;

      const linkId = 'quote-list-download-link';
      const existing = document.getElementById(linkId);
      if (existing) existing.remove();

      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.id = linkId;
      link.href = objectUrl;
      link.download = fileName;
      
      document.body.appendChild(link);
      /* link.click(); */
      if(existing) existing.remove();
      
      URL.revokeObjectURL(objectUrl);
      
      return blob;
    } catch (error) {
      console.error(error);
      return null;
    }
  };


  /* ------------------- Drag and Drop Handlers & Render Functions ------------------- */
  const handleDropInSlot = (slotInfo, itemData, sourceType) => {
    const { lineId, slotIndex } = slotInfo;
    const numericSlotIndex = typeof slotIndex === 'string' ? parseFloat(slotIndex) : slotIndex;
    const slotKey = `${lineId}-${numericSlotIndex}`;

    setModifiedSlot(slotKey);
    
    if (draggingItem && !draggingItem.isDraggable && draggingItem.line == 1) { 
      setDragErrorMessage(t('cannotPlaceLineInElementSlot'));
      setDraggingOverSlot({ lineId, slotIndex: numericSlotIndex, isError: true });
      setTimeout(() => setDragErrorMessage(null), 3000);
      return;
    }
    
    if (sourceType === 'sidebar') {
      const item = leftSidebarItems.find(i => i.id === itemData.id);
      if (item && item.isDraggable) { 
        setElementSlots(prevSlots => {
          const newSlots = {
            ...prevSlots,
            [slotKey]: { 
              ...item, 
              slotId: `${numericSlotIndex}-${item.id}`, 
              slot: numericSlotIndex 
            }
          };
          
          buildAndCheckConfigurationWithSlots(centralStack, newSlots);
          
          return newSlots;
        });
        setIsShifting(true);
        setTimeout(() => setIsShifting(false), 300);
      }
    } else if (sourceType === 'slot' && itemData.slotInfo) {
      const sourceSlotIndex = typeof itemData.slotInfo.slotIndex === 'string' 
        ? parseFloat(itemData.slotInfo.slotIndex) 
        : itemData.slotInfo.slotIndex;
      const sourceKey = `${itemData.slotInfo.lineId}-${sourceSlotIndex}`;
      
      if (sourceKey !== slotKey) {
        setElementSlots(prevSlots => {
          const newSlots = { ...prevSlots };
          const sourceItem = newSlots[sourceKey];
          if (!sourceItem) return newSlots; 
          
          const movedItem = { 
            ...sourceItem, 
            slotId: `${numericSlotIndex}-${sourceItem.id}`, 
            slot: numericSlotIndex 
          };
          delete newSlots[sourceKey];
          newSlots[slotKey] = movedItem;
          
          buildAndCheckConfigurationWithSlots(centralStack, newSlots);
          
          return newSlots;
        });
      }
    }
    
    setDraggingOverSlot(null);
    setDropInfo({ itemIndex: null, position: null, slotInfo: null });
    currentHoveredRef.current = null;
    setDragErrorMessage(null); 
  };

  const handleDragStart = (e, id, sourceType, index = null, slotInfo = null) => {
    // Empêcher le drag si la configuration est validée
    if (isConfigurationValidated()) {
      e.preventDefault();
      return;
    }

    const actualId = parseInt(id); 
    dragItem.current = { id: actualId, index, slotInfo };
    dragSourceType.current = sourceType;
    
    let itemToDrag;
    if (sourceType === 'sidebar') {
      itemToDrag = leftSidebarItems.find(item => item.id === actualId);
    } else if (sourceType === 'stack' && index !== null && centralStack[index]) {
      itemToDrag = centralStack[index];
    } else if (sourceType === 'slot' && slotInfo) {
      const { lineId, slotIndex } = slotInfo;
      itemToDrag = elementSlots[`${lineId}-${slotIndex}`];
    }

    setDraggingItem(itemToDrag); 
    
    if (itemToDrag) { 
        e.dataTransfer.setData("text/plain", String(id)); 
        e.dataTransfer.effectAllowed = "move";
    } else {
        e.preventDefault(); 
    }
  };

  const handleDragEnd = () => {
    setDraggingItem(null);
    setDropInfo({ itemIndex: null, position: null, slotInfo: null });
    setDraggingOverSlot(null);
    currentHoveredRef.current = null;
    setIsDraggingOverContainer(false);
    dragItem.current = null;
    dragSourceType.current = null;
  };
  
  const handleSlotDragOver = (e, lineId, slotIndex) => {
    e.preventDefault();
    
    // Empêcher le drop si la configuration est validée
    if (isConfigurationValidated()) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    
    // Convertir slotIndex en nombre
    const numericSlotIndex = typeof slotIndex === 'string' ? parseFloat(slotIndex) : slotIndex;
    
    if (draggingItem && draggingItem.isDraggable) { 
      e.dataTransfer.dropEffect = "move";
      setDragErrorMessage(null);
      setDraggingOverSlot({ lineId, slotIndex: numericSlotIndex, isError: false });
    } else if (draggingItem && !draggingItem.isDraggable && draggingItem.line === 1) { 
      e.dataTransfer.dropEffect = "none";
      setDragErrorMessage(t('cannotPlaceLineInElementSlot'));
      setDraggingOverSlot({ lineId, slotIndex: numericSlotIndex, isError: true });
      return;
    } else {
      e.dataTransfer.dropEffect = "none";
      return;
    }
    
    setDropInfo({ 
      itemIndex: null, 
      position: null, 
      slotInfo: { lineId, slotIndex: numericSlotIndex } 
    });
  };
  
  const handleContainerDragOver = (e) => {
    e.preventDefault();
    
    // Empêcher le drop si la configuration est validée
    if (isConfigurationValidated()) {
      e.dataTransfer.dropEffect = "none";
      return;
    }
        
    if (draggingItem && !draggingItem.isDraggable && draggingItem.line === 1) {
        e.dataTransfer.dropEffect = "move";
    } else {
        e.dataTransfer.dropEffect = "none";
        return;
    }
    
    setIsDraggingOverContainer(true);
    setDraggingOverSlot(null); 
    
    const containerRect = e.currentTarget.getBoundingClientRect(); 
    const mouseY = e.clientY;
    
    if (centralStack.length === 0) {
      setDropInfo({ itemIndex: 0, position: 'above', slotInfo: null });
    } else {
      let closestIndex = -1;
      let closestDistance = Infinity;
      let closestPosition = 'above';
      
      const stackItems = e.currentTarget.querySelectorAll('.stack-item');
      
      stackItems.forEach((itemNode, idx) => {
        const rect = itemNode.getBoundingClientRect();
        const distanceToTop = Math.abs(mouseY - rect.top);
        const distanceToBottom = Math.abs(mouseY - rect.bottom);
        
        if (distanceToTop < closestDistance) {
          closestDistance = distanceToTop;
          closestIndex = idx;
          closestPosition = 'above';
        }
        if (distanceToBottom < closestDistance) {
          closestDistance = distanceToBottom;
          closestIndex = idx;
          closestPosition = 'below';
        }
      });
      
      if (closestIndex !== -1) {
        setDropInfo({ itemIndex: closestIndex, position: closestPosition, slotInfo: null });
      } else if (mouseY < containerRect.top + 50) {
        setDropInfo({ itemIndex: 0, position: 'above', slotInfo: null });
      } else {
        setDropInfo({ itemIndex: centralStack.length -1, position: 'below', slotInfo: null });
      }
    }
  };
    
  const handleContainerDragLeave = (e) => {
    if (centralContainerRef.current && !centralContainerRef.current.contains(e.relatedTarget)) {
      setIsDraggingOverContainer(false);
      setDropInfo({ itemIndex: null, position: null, slotInfo: null });
    }
  };
  
  const handleSlotDragLeave = (e) => {
    if (e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) {
        setDraggingOverSlot(null);
        setDragErrorMessage(null); 
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    
    if (dropInfo.slotInfo) { 
      handleDropInSlot(dropInfo.slotInfo, dragItem.current, dragSourceType.current);
    } else { 
      if (!isDraggingOverContainer && dropInfo.itemIndex === null) return;
      
      const itemData = dragItem.current;
      const sourceType = dragSourceType.current;
      
      if (!draggingItem || (draggingItem.isDraggable && draggingItem.line !==1) ) { 
        setDragErrorMessage(t('cannotPlaceElementInLineStack'));
        setTimeout(() => setDragErrorMessage(null), 3000);
        handleDragEnd(); 
        return;
      }
      
      let dropIndex;
      if (dropInfo.itemIndex !== null) {
        dropIndex = dropInfo.position === 'above' ? dropInfo.itemIndex : dropInfo.itemIndex + 1;
        if (sourceType === 'stack' && itemData.index !== null && itemData.index < dropIndex) {
          dropIndex -= 1;
        }
      } else {
        dropIndex = centralStack.length;
      }
      
      if (sourceType === 'sidebar') {
        const item = leftSidebarItems.find(i => i.id === itemData.id);
        if (item && item.line === 1) { 
          setIsShifting(true);
          setCentralStack(prevStack => {
            const newStack = [...prevStack];
            const newStackId = `line-new-${Date.now()}-${item.id}`;
            newStack.splice(dropIndex, 0, { ...item, stackId: newStackId, slots: item.slots ?? 0 });
            buildAndCheckConfigurationWithSlots(newStack, elementSlots);
            
            return newStack;
          });
          setTimeout(() => setIsShifting(false), 300);
        }
      } else if (sourceType === 'stack' && itemData.index !== null && centralStack[itemData.index]) {
        setCentralStack(prevStack => {
          const newStack = [...prevStack];
          const [movedItem] = newStack.splice(itemData.index, 1);
          newStack.splice(dropIndex, 0, movedItem);
          
          // Pour le réarrangement, on peut optionnellement checker aussi
          buildAndCheckConfigurationWithSlots(newStack, elementSlots);
          
          return newStack;
        });
      }
    }
    
    handleDragEnd(); 
    setDragErrorMessage(null); 
  };

  const handleRemoveFromSlot = (lineId, slotIndex) => { 
    if (isConfigurationValidated()) {
      return;
    }

    const numericSlotIndex = typeof slotIndex === 'string' ? parseFloat(slotIndex) : slotIndex;
    const slotKey = `${lineId}-${numericSlotIndex}`;
    
    setElementSlots(prevSlots => {
      const newSlots = { ...prevSlots };
      delete newSlots[slotKey];
      buildAndCheckConfigurationWithSlots(centralStack, newSlots);
      
      return newSlots;
    });
      
    setErrorSlots(prevErrorSlots => {
      const newErrorSlots = { ...prevErrorSlots };
      delete newErrorSlots[slotKey];
      return newErrorSlots;
    });
  };

/* //////////////-------- Verify Config  => Generate Image => Generate Quote => Generate Notif //////////////-------- */

  const handleShowValidationPopup = () => setShowValidationPopup(true);
  const handleCloseValidationPopup = () => setShowValidationPopup(false);

  const handlePostQuote = () => { // Function to handle the post quote with image and id
    const lastDoc = documents?.lastDocumentAdded?.[0];
      
    const quoteValidate = {
      configId,
      imageName: lastDoc?.docFilename,
    }

    dispatch(createQuoteRequest(quoteValidate));
    setShowValidationPopup(false);
  }
  
  useEffect(() => {
    dispatch(fetchNotificationsRequest())
    if (configId && 
        documents?.lastDocumentAdded?.length > 0 && 
        documents.lastDocumentAdded[0]?.docOriginalname &&
        quotingProcessStarted) { // Utiliser le flag au lieu des autres conditions
      
      const docName = documents.lastDocumentAdded[0].docOriginalname.split('.')[0];
      if (docName === configId) {
        handlePostQuote(configId);
        handleCreateNotification();
        setQuotingProcessStarted(false); // Reset après traitement
      }
    }
  }, [configId, documents?.lastDocumentAdded, quotingProcessStarted]);
  
  
  const getQuotePdf = () => {

      if (lastCreatedQuote && lastCreatedQuote?.docUrl) {
        get(lastCreatedQuote?.docUrl, { responseType: 'blob' })
        .then((response) => {
          setQuoteBlob(response);
          dispatch(clearQuoteSuccess());
          setIsValidationInProgress(false);
        }).then(() => {
          dispatch(clearDocumentSuccess());
          dispatch(clearVerifyConfigurationSuccess());
          setShowConfirmationPopup(true);
        })
      }
  }

  const handleGenerateImage = () => {
    setQuotingProcessStarted(true); // Marquer le début du processus
    
    createConfigurationImage()
      .then(configImage => {
        if (configImage) {
          const fileName = `${configId}.png`;
          const renamedFile = new File([configImage], fileName, { type: 'image/png' });
          const formData = new FormData();
          formData.append('filedata', renamedFile);
          
          dispatch(
            uploadDocumentRequest(
              formData,
              { type: 'default', params: 'quote' },
              { 'headers': { 'Content-Type': 'multipart/form-data' } }
            )
          );
        }
      })
      .catch(error => {
        console.error('Error in handleGenerateImage:', error);
        setQuotingProcessStarted(false); // Reset en cas d'erreur
      });
  }

  const handleCreateNotification = () => {
    const notifCategory = "QUOTE_NEW";
    const groupsForId = [1, 2]; // Groups to notify

    if (!notifications.some(notification => notification.cat === notifCategory && notification.lib.includes(config.reference))) {
      const obj = {
          userForId: user.id,
          cat: 'QUOTE_NEW',
          lib: `Le Devis de la configuration [${config.reference}] a été crée.`,
          rowVersion: 0,
      }

      if (user.groupId !== 1 && user.groupId != 2) { // If the user is not in group 1 or 2, send notification
        dispatch(updateNotificationRequest(obj));
      }

      for (let groupId of groupsForId) {
        const groupObj = {
          groupForId: groupId,
          cat: 'QUOTE_NEW',
          lib: `Le Devis de la configuration [${config.reference}] a été crée.`,
          rowVersion: 0,
        }
        
        dispatch(updateNotificationRequest(groupObj));
      }
    }
  }
  const handleConfirmValidation = () => {
    setQuotingProcessStarted(true);
    dispatch(verifyConfigurationRequest(configToSend));
  };

  const buildConfigurationObject = useCallback(() => {
    if (!config || !config.lib || centralStack.length === 0) {
      return null;
    }

    const linesWithSlots = centralStack.map((line) => {
      const lineStackId = line.stackId; 
      // Create a new array for matchingSlots from the current elementSlots state
      const currentLineSlots = [];
      for (const key in elementSlots) {
        if (key.startsWith(`${lineStackId}-`)) {
          currentLineSlots.push(elementSlots[key]);
        }
      }
      // Sort the slots by their numeric index
      currentLineSlots.sort((a, b) => a.slot - b.slot);
      
      // Construct the ProjLine object for the API
      return {
        ...line, // Spread all properties from projLine
        ProjProduct: line.ProjProduct, // The line's own product data
        ProjSlots: currentLineSlots.map(slotData => ({
          ...slotData, // Spread all properties from the slot data
          ProjProduct: { ...slotData } // Send the full element data as ProjProduct for the slot
        })), 
      };
    });

    return {
      ...config, // Base config properties (id, lib, etc.)
      ProjLines: linesWithSlots, // UI-derived lines and their slots
    };
  }, [config, centralStack, elementSlots]);

  useEffect(() => {
    if (config && centralStack.length > 0 && !configToSend) {
      const initialConfig = buildConfigurationObject();
      if (initialConfig) {
        setConfigToSend(initialConfig);
      }
    }
  }, [config, centralStack, configToSend, buildConfigurationObject]);

  useEffect( () => {

    if(verifyConfigurationSuccess) {
      const result = processConfigData(
        verifiedConfiguration, // TOCHANGE to verifiedConfiguration when the verification is successful
        configId,
        isInitializedRef,
        prevElementSlotsStrRef,
        prevCentralStackStrRef,
        true // Indicate that this is a verification process
      )
    }

  }, [verifiedConfiguration, verifyConfigurationSuccess])


  
  useEffect( () => {
    const docName = documents?.lastDocumentAdded?.[0]?.docOriginalname.split('.')[0];

    if (docName && docName === configId) {
      getQuotePdf()
    } else {
      dispatch(clearDocumentSuccess());
    }
    
  }, [lastCreatedQuote && lastCreatedQuote.docOriginalname] );
  

  /* ------------------------------------------------------------------------------------------------ */

  useEffect(() => {
    if (centralStack.length > 0 && !elementContainerVisible) {
      setElementContainerVisible(true);
    } else if (centralStack.length === 0 && elementContainerVisible) {
      setElementContainerVisible(false);
    }
  }, [centralStack.length, elementContainerVisible]);

  useEffect(() => {
    if (centralStack.length > 0) {
      const syncHeights = () => {
        const stackItemNodes = document.querySelectorAll('.stack-item');
        const slotsRowNodes = document.querySelectorAll('.elements-container > div:not(:first-child)'); 
        
        Array.from(stackItemNodes).forEach((itemNode, index) => {
          if (slotsRowNodes[index]) {
            const itemHeight = itemNode.offsetHeight;
            slotsRowNodes[index].style.height = `${itemHeight}px`;
            const slotsContainerInRow = slotsRowNodes[index].querySelector('.element-slots');
            if (slotsContainerInRow) {
              slotsContainerInRow.style.height = '100%';
              slotsContainerInRow.style.display = 'flex';
              slotsContainerInRow.style.alignItems = 'center';
            }
          }
        });
      };
      
      // Utiliser ResizeObserver pour détecter les changements de taille
      const resizeObserver = new ResizeObserver(() => {
        syncHeights();
      });
      
      // Observer tous les stack-items
      const stackItemNodes = document.querySelectorAll('.stack-item');
      stackItemNodes.forEach(node => resizeObserver.observe(node));
      
      // Synchronisation initiale avec délai
      setTimeout(syncHeights, 100);
      setTimeout(syncHeights, 300); // Backup
      
      window.addEventListener('resize', syncHeights);
      
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', syncHeights);
      };
    }
  }, [centralStack, elementSlots]);

  useEffect(() => { 
    const handleDocumentDrop = (e) => {
      let isOverCentral = centralContainerRef.current?.contains(e.target);
      let isOverSlots = slotsContainerRef.current?.contains(e.target);
      if (!isOverCentral && !isOverSlots) {
        setIsDraggingOverContainer(false);
        setDropInfo({ itemIndex: null, position: null, slotInfo: null });
      }
    };
    document.addEventListener('drop', handleDocumentDrop);
    return () => document.removeEventListener('drop', handleDocumentDrop);
  }, []);

  // Fonction pour rendre la prévisualisation de l'élément à déposer
  const renderPreview = (position) => {
    if (!draggingItem) return null;


    return (
      <div className="preview-item" style={{
        marginBottom: position === 'above' ? '8px' : '0',
        marginTop: position === 'below' ? '8px' : '0',
      }}>
        <img
          src={draggingItem.img || transparentImage}
          alt={draggingItem.lib}
          className="preview-image"
        />
      </div>
    );
  };

  // Render the slot for an element
  const renderElementSlot = (lineId, slotIndex, hasElement = false, inPopup = false) => {
    const numericSlotIndex = typeof slotIndex === 'string' ? parseFloat(slotIndex) : slotIndex;
    
    const slotKey = `${lineId}-${numericSlotIndex}`;
    const element = elementSlots[slotKey];
    
    // Si pas d'élément à cet index, ne pas afficher de slot vide pour les demi-slots
    if (!element && numericSlotIndex % 1 !== 0) {
      return null; // Ne pas afficher les demi-slots vides
    }
    
    const isBeingDraggedOver = draggingOverSlot && 
                              draggingOverSlot.lineId === lineId && 
                              draggingOverSlot.slotIndex === numericSlotIndex;
    const isError = isBeingDraggedOver && draggingOverSlot.isError;
    const isConfigError = errorSlots && Object.keys(errorSlots).includes(slotKey) && errorSlots[slotKey] !== undefined;
    
    // Classes CSS spéciales pour les éléments 0.5
    const isHalfSlot = element && element.nbModules === 0.5;
    
    // Vérifier si la configuration est validée
    const isValidated = isConfigurationValidated();
    
    const slotClasses = [
      'element-slot',
      element ? 'filled' : '',
      isHalfSlot ? 'half-slot' : '', // Classe spéciale pour les demi-slots
      isBeingDraggedOver && !inPopup ? 'dragging-over' : '',
      isConfigError ? 'error-slot' : '',
      inPopup ? 'read-only' : '',
      isValidated ? 'validated-config' : '' // Nouvelle classe pour les configs validées
    ].filter(Boolean).join(' ');
    
    return (
      <div
        key={`slot-${lineId}-${numericSlotIndex}`}
        className={slotClasses}
        onDragOver={!inPopup && !isValidated ? (e) => handleSlotDragOver(e, lineId, numericSlotIndex) : undefined}
        onDragLeave={!inPopup && !isValidated ? handleSlotDragLeave : undefined}
        onDrop={!inPopup && !isValidated ? handleDrop : undefined}
      >
        {element ? (
          <div
            className="slot-element-container"
            draggable={!inPopup && !isValidated}
            onDragStart={!inPopup && !isValidated ? (e) => handleDragStart(e, element.id.toString(), 'slot', null, { lineId, slotIndex: numericSlotIndex }) : undefined}
            onDragEnd={!inPopup && !isValidated ? handleDragEnd : undefined}
            style={{
              cursor: isValidated ? 'not-allowed' : 'grab'
            }}
          >
            <img
              id={`projSlotImage${element.ref}-${lineId}-${numericSlotIndex}`.replace(/\./g, '-')}
              src={element.img || transparentImage}
              alt={element.lib}
              className="slot-element-image"
            />
            <UncontrolledTooltip
              className={`slot-tooltip`}
              innerClassName={slotClasses.includes('error-slot') && !inPopup ? 'error-tooltip' : ''}
              transition={{ timeout: 300 }}
              placement="top"
              fade={false}
              target={`projSlotImage${element.ref}-${lineId}-${numericSlotIndex}`.replace(/\./g, '-')}
            >
              {slotClasses.includes('error-slot') && !inPopup
                  ? errorSlots[slotKey] || t('errorInSlot')
                  : isValidated 
                    ? `${element.lib} - ${t('configurationCompleted')}`
                    : element.lib}
            </UncontrolledTooltip>
            {!inPopup && !isValidated && (
              <button
                className="remove-button"
                onClick={() => handleRemoveFromSlot(lineId, numericSlotIndex)}
              >
                ×
              </button>
            )}
          </div>
        ) : (
          <span className={`slot-placeholder ${isError && !inPopup ? 'error' : ''}`}>
            {isError && !inPopup ? t('cannotPlaceHere') 
              : inPopup ? t('emptySlot') 
              : isValidated ? t('configurationCompleted')
              : t('dropElementHere')}
          </span>
        )}
        
        {isBeingDraggedOver && draggingItem && draggingItem.isDraggable && !element && !isError && !inPopup && !isValidated && (
          <div className="slot-preview">
            <img
              src={draggingItem.img || transparentImage}
              alt={draggingItem.lib}
              className="slot-element-image"
            />
          </div>
        )}
        
        {isBeingDraggedOver && isError && !inPopup && (
          <div className="error-indicator">
            <span className="error-icon">✕</span>
          </div>
        )}
      </div>
    );
  };


   // Render overlay elements on a line image
  const renderElementOverlays = (lineItem, inPopup = false) => {
    if (!lineItem || !lineItem.stackId) {
      return null;
    }
    
    if (lineItem.slots == null || !lineItem.ProjProduct?.slotsPlacement) {
      return null;
    }
    
    // Récupérer les éléments de cette ligne avec leurs vraies positions
    const elements = getElementsForLine(lineItem.stackId);
    
    if (!elements || !Array.isArray(elements) || elements.length === 0) {
      return null;
    }
    
    // Parser les coordonnées des slots
    let slotsPlacement;
    try {
      slotsPlacement = JSON.parse(lineItem.ProjProduct?.slotsPlacement);
    } catch (error) {
      return null;
    }
    
    if (!Array.isArray(slotsPlacement) || slotsPlacement.length === 0) {
      return null;
    }

    const imageSizeReference = { width: 100, height: 100 };
    const occupiedSlots = new Set();
    const overlays = [];
    
    // Trier les éléments par position
    const sortedElements = elements
      .filter(element => element && typeof element.slotIndex === 'number' && !isNaN(element.slotIndex))
      .sort((a, b) => a.slotIndex - b.slotIndex);
    
    // Traiter chaque élément
    sortedElements.forEach((element, elementIndex) => {
      if (!element || typeof element.slotIndex !== 'number') {
        return;
      }
      
      const slotIndex = element.slotIndex;
      const nbModules = element.nbModules || 1;
      
      // Vérifier si ce slot est déjà occupé
      if (occupiedSlots.has(slotIndex)) {
        return;
      }
      
      // ===== ÉLÉMENTS DE 0.5 MODULE =====
      if (nbModules === 0.5) {
        const baseSlotIndex = Math.floor(slotIndex);
        
        if (baseSlotIndex >= slotsPlacement.length) {
          return;
        }
        
        const slotCoords = slotsPlacement[baseSlotIndex];
        if (!Array.isArray(slotCoords) || slotCoords.length < 4) {
          return;
        }
        
        const [x1, y1, x2, y2] = slotCoords;
        const slotWidth = x2 - x1;
        const halfWidth = slotWidth / 2;
        
        let actualX1, actualX2;
        
        if (slotIndex % 1 === 0) {
          // Première moitié (ex: position 0 dans slot 0)
          actualX1 = x1;
          actualX2 = x1 + halfWidth;
        } else {
          // Deuxième moitié (ex: position 0.5 dans slot 0)
          actualX1 = x1 + halfWidth;
          actualX2 = x2;
        }
        
        occupiedSlots.add(slotIndex);
        const containerStyle = {
          position: 'absolute',
          left: `${(actualX1 / imageSizeReference.width) * 100}%`,
          top: `${(y1 / imageSizeReference.height) * 100}%`,
          width: `${((actualX2 - actualX1) / imageSizeReference.width) * 100}%`,
          height: `${((y2 - y1) / imageSizeReference.height) * 100}%`,
          zIndex: 5,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        };
        
        overlays.push(
          <div 
            key={`overlay-${lineItem.stackId}-${slotIndex}`} 
            className="element-overlay-direct half-module"
            style={containerStyle}
          >
            <img 
              src={element.img || transparentImage} 
              alt={element.lib || 'Element'}
              className={(!inPopup) ? "element-overlay-img" : "element-overlay-popup-img"}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                maxWidth: 'none',
                maxHeight: 'none',
              }}
            />
          </div>
        );
      }
      
      // ===== ÉLÉMENTS DE 1 MODULE =====
      else if (nbModules === 1) {
        
        if (slotIndex % 1 === 0) {
          // *** POSITION ENTIÈRE (ex: 0, 1, 2) - occupe tout le slot ***
          const slotCoordIndex = Math.floor(slotIndex);
          
          if (slotCoordIndex >= slotsPlacement.length) {
            return;
          }
          
          const slotCoords = slotsPlacement[slotCoordIndex];
          if (!Array.isArray(slotCoords) || slotCoords.length < 4) {
            return;
          }
          
          const [x1, y1, x2, y2] = slotCoords;
          
          occupiedSlots.add(slotIndex);
          
          const containerStyle = {
            position: 'absolute',
            left: `${(x1 / imageSizeReference.width) * 100}%`,
            top: `${(y1 / imageSizeReference.height) * 100}%`,
            width: `${((x2 - x1) / imageSizeReference.width) * 100}%`,
            height: `${((y2 - y1) / imageSizeReference.height) * 100}%`,
            zIndex: 5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          };
          
          overlays.push(
            <div 
              key={`overlay-${lineItem.stackId}-${slotIndex}`} 
              className="element-overlay-direct single-module-integer"
              style={containerStyle}
            >
              <img 
                src={element.img || transparentImage} 
                alt={element.lib || 'Element'}
                className={(!inPopup) ? "element-overlay-img" : "element-overlay-popup-img"}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill',
                  maxWidth: 'none',
                  maxHeight: 'none',
                }}
              />
            </div>
          );
          
        } else {
          // *** POSITION DÉCIMALE (ex: 8.5, 9.5) - s'étend sur 2 slots ***
          const startSlotIndex = Math.floor(slotIndex);  // 8
          const endSlotIndex = startSlotIndex + 1;       // 9
          
          if (startSlotIndex >= slotsPlacement.length || endSlotIndex >= slotsPlacement.length) {
            return;
          }
          
          const startSlotCoords = slotsPlacement[startSlotIndex];
          const endSlotCoords = slotsPlacement[endSlotIndex];
          
          if (!Array.isArray(startSlotCoords) || startSlotCoords.length < 4 ||
              !Array.isArray(endSlotCoords) || endSlotCoords.length < 4) {
            return;
          }
          
          const [x1Start, y1Start, x2Start, y2Start] = startSlotCoords;
          const [x1End, y1End, x2End, y2End] = endSlotCoords;
          
          // Calculer les positions : du milieu du premier slot au milieu du deuxième
          const halfWidthStart = (x2Start - x1Start) / 2;
          const halfWidthEnd = (x2End - x1End) / 2;
          
          const actualX1 = x1Start + halfWidthStart;  // Milieu du slot de départ
          const actualX2 = x1End + halfWidthEnd;      // Milieu du slot de fin
          const actualY1 = Math.min(y1Start, y1End);
          const actualY2 = Math.max(y2Start, y2End);
          
          // Marquer les slots occupés
          occupiedSlots.add(slotIndex);               // 8.5
          occupiedSlots.add(startSlotIndex + 1);      // 9.0
          
          const containerStyle = {
            position: 'absolute',
            left: `${(actualX1 / imageSizeReference.width) * 100}%`,
            top: `${(actualY1 / imageSizeReference.height) * 100}%`,
            width: `${((actualX2 - actualX1) / imageSizeReference.width) * 100}%`,
            height: `${((actualY2 - actualY1) / imageSizeReference.height) * 100}%`,
            zIndex: 5,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          };
          
          overlays.push(
            <div 
              key={`overlay-${lineItem.stackId}-${slotIndex}`} 
              className="element-overlay-direct single-module-decimal"
              style={containerStyle}
            >
              <img 
                src={element.img || transparentImage} 
                alt={element.lib || 'Element'}
                className={(!inPopup) ? "element-overlay-img" : "element-overlay-popup-img"}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'fill',
                  maxWidth: 'none',
                  maxHeight: 'none',
                }}
              />
            </div>
          );
        }
      }
      
      // ===== ÉLÉMENTS MULTI-MODULES (> 1) =====
      else if (nbModules > 1) {
        
        // Vérifier que tous les slots nécessaires sont disponibles
        let allSlotsAvailable = true;
        const slotsNeeded = [];
        
        for (let i = 0; i < nbModules; i += 0.5) {
          const neededSlot = slotIndex + i;
          const floorIndex = Math.floor(neededSlot);
          
          if (occupiedSlots.has(neededSlot) || floorIndex >= slotsPlacement.length) {
            allSlotsAvailable = false;
            break;
          }
          slotsNeeded.push(neededSlot);
        }
        
        if (!allSlotsAvailable) {
          return;
        }
        
        // Marquer tous les slots comme occupés
        slotsNeeded.forEach(slot => occupiedSlots.add(slot));
        
        // Calculer les coordonnées étendues
        const startSlotIndex = Math.floor(slotIndex);
        const endSlotIndex = Math.floor(slotIndex + nbModules - 0.001); // -0.001 pour éviter les erreurs de floating point
        
        let minX1 = Infinity, minY1 = Infinity, maxX2 = 0, maxY2 = 0;
        
        // Parcourir tous les slots physiques que l'élément occupe
        for (let currentSlot = startSlotIndex; currentSlot <= endSlotIndex; currentSlot++) {
          if (currentSlot >= slotsPlacement.length) continue;
          
          const slotCoords = slotsPlacement[currentSlot];
          if (!Array.isArray(slotCoords) || slotCoords.length < 4) {
            continue;
          }
          
          const [x1, y1, x2, y2] = slotCoords;
          
          let adjustedX1 = x1;
          let adjustedX2 = x2;
          
          // Ajuster le début si l'élément commence au milieu d'un slot
          if (currentSlot === startSlotIndex && slotIndex % 1 !== 0) {
            const halfWidth = (x2 - x1) / 2;
            adjustedX1 = x1 + halfWidth;
          }
          
          // Ajuster la fin si l'élément se termine au milieu d'un slot
          if (currentSlot === endSlotIndex) {
            const endPosition = slotIndex + nbModules;
            if (endPosition % 1 !== 0) {
              const partialWidth = (x2 - x1) * (endPosition % 1);
              adjustedX2 = x1 + partialWidth;
            }
          }
          
          minX1 = Math.min(minX1, adjustedX1);
          minY1 = Math.min(minY1, y1);
          maxX2 = Math.max(maxX2, adjustedX2);
          maxY2 = Math.max(maxY2, y2);
        }
        
        if (minX1 === Infinity || minY1 === Infinity) {
          return;
        }
        
        const containerStyle = {
          position: 'absolute',
          left: `${(minX1 / imageSizeReference.width) * 100}%`,
          top: `${(minY1 / imageSizeReference.height) * 100}%`,
          width: `${((maxX2 - minX1) / imageSizeReference.width) * 100}%`,
          height: `${((maxY2 - minY1) / imageSizeReference.height) * 100}%`,
          zIndex: 5,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        };
        
        overlays.push(
          <div 
            key={`overlay-${lineItem.stackId}-${slotIndex}`} 
            className="element-overlay-direct multi-module"
            style={containerStyle}
          >
            <img 
              src={element.img || transparentImage} 
              alt={element.lib || 'Element'}
              className={(!inPopup) ? "element-overlay-img" : "element-overlay-popup-img"}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                maxWidth: 'none',
                maxHeight: 'none',
              }}
            />
          </div>
        );
      }
    });
    
    return overlays;
  };

  const getElementsForLine = (lineId) => {
    // Vérification de sécurité pour lineId
    if (!lineId) {
      return [];
    }
    
    const elements = [];
    
    // Get the line item avec vérification
    const lineItem = centralStack?.find(item => item?.stackId === lineId);
    
    if (!lineItem || lineItem.slots == null) {
      return elements;
    }
    
    // Vérification de sécurité pour elementSlots
    if (!elementSlots || typeof elementSlots !== 'object') {
      return elements;
    }
    
    // Parcourir tous les slots possibles incluant les demi-slots
    let currentSlot = 0;
    while (currentSlot < lineItem.slots) {
      // Vérifier le slot entier
      const wholeSlotKey = `${lineId}-${currentSlot}`;
      if (elementSlots[wholeSlotKey]) {
        elements.push({
          ...elementSlots[wholeSlotKey],
          slotIndex: currentSlot
        });
      }
      
      // Vérifier le demi-slot
      const halfSlotKey = `${lineId}-${currentSlot + 0.5}`;
      if (elementSlots[halfSlotKey]) {
        elements.push({
          ...elementSlots[halfSlotKey],
          slotIndex: currentSlot + 0.5
        });
      }
      
      currentSlot += 1;
    }
    
    return elements;
  };
  
  // Render the stack items with previews
  const renderStack = (inPopup = false) => {
    // If stack is empty, render empty container with drop zone
    if (centralStack.length === 0) {
      return (
        <div className={`empty-container ${isDraggingOverContainer && !inPopup ? 'dragging-over' : ''}`}>
          <span className="empty-container-label">
            {inPopup ? t('noLinesSelected') : t('dropLinesHere')}
          </span>
          
          {/* Preview item when dragging over empty container */}
          {isDraggingOverContainer && draggingItem && draggingItem.type === 'line' && !inPopup && (
            <div className="preview-item">
              <img
                src={draggingItem.img || transparentImage}
                alt={draggingItem.lib}
                className="preview-image"
              />
            </div>
          )}
        </div>
      );
    }

    // Create array to hold all rendered items including previews
    const renderedItems = [];
    
    // Correction: Ne pas ajouter de prévisualisation au début si nous sommes en train de glisser un élément de la pile
    const isFirstItemBeingDragged = dragSourceType.current === 'stack' && 
                                   dragItem.current && 
                                   dragItem.current.index === 0;
    
    // Add preview at the beginning if needed
    if (dropInfo.itemIndex === 0 && 
        dropInfo.position === 'above' && 
        draggingItem && 
        draggingItem.type === 'line' &&
        !isFirstItemBeingDragged &&
        !inPopup) {
      renderedItems.push(
        <div key="preview-start">{renderPreview('above')}</div>
      );
    }
    
    // Render each stack item and any needed previews
    centralStack.forEach((item, i) => {
      if (item?.ProjProduct?.ref == 'EMPTY') {
        return;
      }

      const isBeingDragged = dragSourceType.current === 'stack' && 
                           dragItem.current && 
                           dragItem.current.index === i;
      
      // Add preview before this item if needed
      if (dropInfo.itemIndex === i && 
          dropInfo.position === 'above' && 
          draggingItem && 
          draggingItem.type === 'line' &&
          !isBeingDragged &&
          !(dropInfo.itemIndex === 0 && i === 0) &&
          !inPopup) {
        renderedItems.push(
          <div key={`preview-before-${i}`}>{renderPreview('above')}</div>
        );
      }
      
      // Skip rendering the item being dragged from stack if not in popup
      if (!isBeingDragged || inPopup) {
        renderedItems.push(
          <div 
            key={`line-${item.stackId}`}
            className={`stack-item ${inPopup ? 'read-only' : ''}`}
          >
            <div className="stack-item-container">
              {/* Base line image */}
              <img
                src={item.img || transparentImage}
                alt={item.lib}
                title={item.lib}
                className="stack-item-image"
              />
              
              {/* Overlay elements on the line image */}
              {renderElementOverlays(item)}
            </div>
            
            {/* Render indicator lines when hovering over this item */}
            {dropInfo.itemIndex === i && !isBeingDragged && !inPopup && (
              <div className={`drop-indicator ${dropInfo.position}`} />
            )}
          </div>
        );
      } else {
        // Add a placeholder for the dragged element
        renderedItems.push(
          <div key={`dragged-placeholder-${i}`} className="dragged-placeholder" />
        );
      }
      
      // Add preview after this item if needed
      if (dropInfo.itemIndex === i && 
          dropInfo.position === 'below' && 
          draggingItem && 
          draggingItem.type === 'line' &&
          !isBeingDragged &&
          !(i === centralStack.length - 1 && dropInfo.itemIndex === centralStack.length - 1) &&
          !inPopup) {
        renderedItems.push(
          <div key={`preview-after-${i}`}>{renderPreview('below')}</div>
        );
      }
    });
    
    // Correction: Ne pas ajouter de prévisualisation à la fin si nous sommes en train de glisser le dernier élément
    const isLastItemBeingDragged = dragSourceType.current === 'stack' && 
                                  dragItem.current && 
                                  dragItem.current.index === centralStack.length - 1;
    
    // Add a preview at the end if needed
    if (dropInfo.itemIndex === centralStack.length - 1 && 
        dropInfo.position === 'below' && 
        draggingItem && 
        draggingItem.type === 'line' &&
        !isLastItemBeingDragged &&
        !inPopup) {
      renderedItems.push(
        <div key="preview-end">{renderPreview('below')}</div>
      );
    }
    
    return renderedItems;
  };
  
  const renderAllSlots = (inPopup = false) => {
    if (centralStack.length === 0) {
      return (
        <div className="empty-slots-container">
          <span className="empty-container-label">
            {inPopup ? t('noSlotsAvailable') : t('addLinesToSeeSlots')}
          </span>
        </div>
      );
    }
    
    return centralStack.map((item, i) => {
      if (item?.ProjProduct?.ref == 'EMPTY') {
        return;
      }

      return (
      <div 
        key={`slots-row-${item.stackId}`}
        className={`slots-row ${i === centralStack.length - 1 ? 'slots-row-last' : ''}`}
      >
        {item.slots != null ? (
          renderLineSlots(item, inPopup)
        ) : (
          <div className="no-slots-message">
            {t('noSlotsForThisLine')}
          </div>
        )}
      </div>
    )});
  };
  
  const renderLineSlots = (lineItem, inPopup = false) => {
    const slots = lineItem.slots || 0;
    
    if (slots === 0) return (
      <span className="empty-container-label">
        {t('noSlotsAvailable')}
      </span>
    );

    if (slots > 10) return (
      <span className="empty-container-label">
        {t('noSlotsAvailable')}
      </span>
    );

    // Générer TOUS les indices possibles incluant les demi-slots
    const generateAllSlotIndices = (maxSlots) => {
      const indices = [];
      
      // Parcourir avec des incréments de 0.5 pour capturer tous les slots possibles
      for (let i = 0; i < maxSlots; i += 0.5) {
        indices.push(i);
      }
      
      return indices;
    };

    const allSlotIndices = generateAllSlotIndices(slots);

    if (lineItem?.ProjProduct?.ref === '350814') {
      return (
        <div className="element-slots">
          {allSlotIndices.filter(index => index > 4).map((index) => 
            renderElementSlot(lineItem.stackId, index, false, inPopup)
          )}
        </div>
      );
    }

    return (
      <div className="element-slots">
        {allSlotIndices.map((index) => 
          renderElementSlot(lineItem.stackId, index, false, inPopup)
        )}
      </div>
    );
  };

  // Popup de validation pour afficher la configuration actuelle
/*  // to remove, to check logic and code
  useEffect(() => {
    if (!showValidationPopup) return;
    
    
    // Get the keys of dictCaches in array
    const lineRefValues = Object.keys(dictCaches);
    
    // Iterate through centralStack
    // Find all lines where ProjSlots.length < slots and ProjProduct.ref exists in lineRefValues
    // Fill empty slots in ProjSlots with the corresponding cache 
    // from allCaches where allCaches.ref === dictCaches[ProjProduct.ref]
    const modifiedElementSlots =JSON.parse(JSON.stringify(elementSlots));
    const modifiedCentralStack = JSON.parse(JSON.stringify(centralStack)).map((item) => {
      const lineRef = item.ProjProduct.ref;
      const lineCache = dictCaches[lineRef];
      const lineSlots = item.ProjSlots || [];
      
      // Check if the line has empty slots and if the reference exists in dictCaches
      if (lineSlots.length < item.slots && lineRefValues.includes(lineRef)) {
        const cacheItem = cachesWithImages.find(cache => cache.ref === lineCache);

        if (cacheItem) {
          for (let i = lineSlots.length; i < item.slots ; i++) {
            // fill ProjProduct with the cache item
            // the slot field with i +1
            lineSlots.push({     
              ...cacheItem,         
              ProjProduct: cacheItem,
              slot: i,
              slotId: `${i}-${cacheItem.id}`, 
            });
            modifiedElementSlots[`line-${item.id}-${i}`] = {
              ...cacheItem,
              slot: i,
              slotId: `${i}-${cacheItem.id}`, 
            }
          }
        }
      }
      
      return { ...item, ProjSlots: lineSlots };
    });
    setCachedCentralStack(modifiedCentralStack);
    setCachedElementSlots(modifiedElementSlots);
  }, [showValidationPopup]); // Dependencies

   */
// Updated renderValidationPopup function (remove the state update logic)

  
  // Popup de confirmation d'envoi
  const RenderConfirmationPopup = () => {
    
    const handleDownloadTechnicalSheet = () => {
      if (quoteBlob) {
        // Create object URL from blob
        const blobUrl = URL.createObjectURL(quoteBlob);

        const linkId = 'quote-download-link';
        const existing = document.getElementById(linkId);
        if (existing) existing.remove();
        
        // Create temporary download link
        const link = document.createElement('a');
        link.id = linkId;
        link.href = blobUrl;
        link.download = `devis_${config?.reference || 'configuration'}.pdf`;
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        if(existing) existing.remove();
        
        // Clean up object URL
        URL.revokeObjectURL(blobUrl);
      }
    };

    
    const handleCloseConfirmationPopup = () => {
      setShowConfirmationPopup(false);
    };
    
    return (
      <Modal isOpen={showConfirmationPopup} toggle={handleCloseConfirmationPopup} size="lg">
      <ModalHeader toggle={handleCloseConfirmationPopup}>
        {t('quoteCreated') || 'Devis créé'}
      </ModalHeader>

      <ModalBody>
        <p className="confirmation-message">
          {t('technicalSheetAndQuoteSent') || 'La fiche technique et le devis ont bien été envoyés par email au client.'}
        </p>

        <div className="technical-sheet-container">
          <div className="technical-sheet-preview">
            { quoteBlob ? (
                <object
                  data={URL.createObjectURL(quoteBlob)}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  className="technical-sheet-iframe"
                >
                  <p><a href={URL.createObjectURL(quoteBlob)} download>{t('download')}</a></p>
                </object>
              ) : (
                <Spinner size={"sm"} />
              )
              }
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={handleCloseConfirmationPopup}>
          {t('close') || 'Fermer'}
        </Button>
        <Button color="primary" onClick={handleDownloadTechnicalSheet}>
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span>{'Télécharger'}</span>
        </Button>
      </ModalFooter>
    </Modal>
    );
  };

  // Ajoutez cet useEffect pour synchroniser les hauteurs dans la popup
  useEffect(() => { // Sync heights in validation popup
    if (showValidationPopup && centralStack.length > 0) {
      const syncPopupHeights = () => {
        const popupStackItems = document.querySelectorAll('.popup-stack-item');
        const popupSlotsRows = document.querySelectorAll('.popup-slots-row');
        Array.from(popupStackItems).forEach((itemNode, index) => {
          if (popupSlotsRows[index]) {
            popupSlotsRows[index].style.height = `${itemNode.offsetHeight}px`;
            const slotsContainer = popupSlotsRows[index].querySelector('.popup-element-slots');
            if (slotsContainer) {
              slotsContainer.style.display = 'flex';
              slotsContainer.style.alignItems = 'center';
              slotsContainer.style.height = '100%';
            }
          }
        });
      };
      setTimeout(syncPopupHeights, 100);
      window.addEventListener('resize', syncPopupHeights);
      return () => window.removeEventListener('resize', syncPopupHeights);
    }
  }, [showValidationPopup, centralStack.length]);

  // Récupérer les produits de la line 0
    return (
    <div className="configurator">
      {/* Sidebar gauche - Affiche les produits groupés par type */}
      <div className="sidebar-container">
        <h2 className="sidebar-title" style={{ padding: '14px 14px 0 12px' }}>
          {t('availableProducts')}
        </h2>
        <div className="sidebar-content" style={{ 
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 140px)',
          overflow: 'auto',
          padding: '12px',
          paddingBottom: '110px' 
        }}>
          {Object.entries(groupedProducts).map(([category, productsInCategory]) => (
            <div key={category} className="product-category" style={{ marginBottom: '10px' }}>
              <h2 className="sidebar-title-category">{category}</h2>
              <div className="category-products" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '8px' }}>
                {productsInCategory.map(product => (
                  <div 
                    key={product.id} 
                    className="product-item" 
                    draggable={!isConfigurationValidated()} // Désactiver le drag si validé
                    onDragStart={!isConfigurationValidated() ? (e) => handleDragStart(e, String(product.id), 'sidebar') : undefined} 
                    onDragEnd={!isConfigurationValidated() ? handleDragEnd : undefined}
                    style={{ 
                      padding: '4px', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '6px', 
                      cursor: isConfigurationValidated() ? 'not-allowed' : 'grab', 
                      backgroundColor: isConfigurationValidated() ? '#f3f4f6' : 'white', 
                      opacity: isConfigurationValidated() ? 0.6 : 1,
                      transition: 'all 0.2s ease',
                      minHeight: '80px'
                    }}
                  >
                    <img src={product.img || transparentImage} alt={product.lib} style={{ width: '48px', height: '48px', objectFit: 'contain', display: 'block', margin: '0 auto 8px' }}/>
                    <div style={{ fontSize: '10px', textAlign: 'center', color: '#4b5563', wordBreak: 'break-word' }}>{product.lib?.split('-')[0]?.trim()}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Zone centrale */}
      <div className="central-container" style={{ 
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 140px)',
          overflow: 'auto',
          padding: '12px',
          paddingBottom: '10px',
        }}>
        <h1 className="central-title">{config?.lib || t('loadingConfiguration')}</h1>
        <div className={`central-layout ${centralStack.length > 0 ? 'has-lines' : ''}`}>
          <div ref={centralContainerRef} className={`stack-container ${centralStack.length > 0 ? 'has-lines' : 'empty'}`}
            onDragOver={handleContainerDragOver} onDragLeave={handleContainerDragLeave} onDrop={handleDrop}>
            {renderStack()}
          </div>
          <div ref={slotsContainerRef} className={`elements-container ${elementContainerVisible ? 'visible' : 'hidden'}`}>
            <h3 className="elements-header">{t('placeInSlots')}</h3>
            {renderAllSlots()}
          </div>
        </div>
      </div>

      {/* Sidebar droite */}
      <div className="sidebar-container right-sidebar">
        <div className="sidebar-header">
          <h2 className="sidebar-title">{t('stackedItems')}</h2>
        </div>

        <div className="sidebar-content">
          {centralStack.length > 0 ? (
            <Card className="stacked-items-list">
              {centralStack.map((item, index) => {

                if (item?.ProjProduct?.ref == 'EMPTY') { // Skip line with ref 'EMPTY'
                  return;
                }

                return (
                <CardBody key={item.stackId+'-stacked-item'} className="stacked-item">
                  <div className="stacked-item-content">
                    {/* Partie gauche : Image et informations de la ligne */}
                    <div className={`stacked-item-info ${item.slots > 0 ? 'with-border' : ''}`}
                      style={{ 
                        minHeight: item.slots > 0 ? `${item.slots * 38}px` : 'auto',
                        position: 'relative' 
                      }}
                    >
                      <div className="stacked-item-header">
                        <div className="stacked-item-image-container">
                          <img 
                            src={item.img || transparentImage} 
                            alt={item.lib} 
                            className="stacked-item-image"
                          />
                          {/* Overlay elements removed from right sidebar */}
                        </div>
                        <div>
                          <div className="stacked-item-details">{item.lib}</div>
                          <div className="stacked-item-position">
                            {t('position')}: {index + 1}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Partie droite : Slots avec éléments */}
                    {item.slots > 0 && (
                      <div className="stacked-slots">
                        {(() => {
                          const slots = [];
                          
                          // Récupérer tous les éléments de cette ligne depuis l'état elementSlots
                          const lineElements = [];
                          
                          // Parcourir elementSlots pour trouver tous les éléments de cette ligne
                          Object.keys(elementSlots).forEach(slotKey => {
                            if (slotKey.startsWith(`${item.stackId}-`)) {
                              const slotIndex = parseFloat(slotKey.split('-').pop());
                              const element = elementSlots[slotKey];
                              if (element) {
                                lineElements.push({
                                  ...element,
                                  slotIndex: slotIndex,
                                  originalSlotKey: slotKey
                                });
                              }
                            }
                          });
                          
                          // Trier les éléments par leur index de slot
                          lineElements.sort((a, b) => a.slotIndex - b.slotIndex);
                          
                          // Créer un Set des slots occupés pour éviter les doublons
                          const occupiedSlots = new Set();
                          
                          // Marquer tous les slots occupés par chaque élément
                          lineElements.forEach(element => {
                            const nbModules = element.nbModules || 1;
                            const startSlot = element.slotIndex;
                            
                            // Marquer tous les slots occupés par cet élément (avec précision 0.5)
                            for (let i = 0; i < nbModules; i += 0.5) {
                              occupiedSlots.add(startSlot + i);
                            }
                          });
                          
                          // Générer l'affichage pour chaque élément
                          lineElements.forEach((element, index) => {
                            const nbModules = element.nbModules || 1;
                            const startSlot = element.slotIndex;
                            const endSlot = startSlot + nbModules;
                            
                            let displayRange;
                            if (nbModules === 0.5) {
                              // Pour 0.5 module : afficher start-end
                              displayRange = `${startSlot}-${endSlot}`;
                            } else if (nbModules === 1) {
                              // Pour 1 module : 
                              // Si le slot commence à une position décimale (ex: 8.5), afficher juste cette position
                              // Si le slot commence à une position entière, afficher juste le numéro entier
                              if (startSlot % 1 === 0) {
                                // Position entière (ex: 0, 1, 2...)
                                displayRange = `${startSlot}`;
                              } else {
                                // Position décimale (ex: 8.5, 9.5...)
                                displayRange = `${startSlot}`;
                              }
                            } else {
                              // Pour plusieurs modules : afficher start-end
                              displayRange = `${startSlot}-${endSlot}`;
                            }

                            slots.push(
                              <React.Fragment key={`element-${element.originalSlotKey}`}>
                                {slots.length > 0 && (
                                  <div style={{
                                    height: '0px',
                                    borderTop: '1px dashed #ccc',
                                    margin: '4px 0',
                                  }}></div>
                                )}
                                <div className="stacked-slot">
                                  <div className="stacked-slot-number">{displayRange}</div>
                                  <div className="stacked-slot-content">
                                    <div className="stacked-slot-element">
                                      <img
                                        src={element.img || transparentImage}
                                        alt={element.lib}
                                        title={element.lib}
                                        className="stacked-slot-element-image"
                                      />
                                    </div>
                                    <div className="stacked-slot-element-label">{element.lib}</div>
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          });
                          // Maintenant, ajouter les slots vides
                          for (let slotIndex = 0; slotIndex < item.slots; slotIndex += 1) {
                            // Vérifier si ce slot entier (positions x.0 et x.5) est complètement libre
                            const wholeSlotOccupied = occupiedSlots.has(slotIndex) || occupiedSlots.has(slotIndex + 0.5);
                            // Vérifier aussi si le slot est partiellement occupé par un élément qui commence avant
                            let partiallyOccupied = false;
                            for (let checkPos = slotIndex; checkPos < slotIndex + 1; checkPos += 0.5) {
                              if (occupiedSlots.has(checkPos)) {
                                partiallyOccupied = true;
                                break;
                              }
                            }
                            
                            const isSlotFree = !wholeSlotOccupied && !partiallyOccupied;
                            
                            if (isSlotFree) {
                              slots.push(
                                <React.Fragment key={`empty-${slotIndex}`}>
                                  {slots.length > 0 && (
                                    <div style={{
                                      height: '0px',
                                      borderTop: '1px dashed #ccc',
                                      margin: '4px 0',
                                    }}></div>
                                  )}
                                  <div className="stacked-slot">
                                    <div className="stacked-slot-number">{slotIndex}</div>
                                    <div className="stacked-slot-content">
                                      <div className="stacked-slot-empty">
                                        <span className="stacked-slot-empty-label">{t('empty')}</span>
                                      </div>
                                      <div className="stacked-slot-empty-text">{t('emptySlot')}</div>
                                    </div>
                                  </div>
                                </React.Fragment>
                              );
                            }
                          }
                          
                          // Trier tous les slots par leur position pour un affichage ordonné
                          return slots.sort((a, b) => {
                            // Extraire la position depuis la clé
                            const getPosition = (slot) => {
                              const key = slot.key;
                              if (key.startsWith('element-')) {
                                // Pour les éléments, extraire la position depuis la clé du slot
                                const slotKey = key.replace('element-', '');
                                const parts = slotKey.split('-');
                                return parseFloat(parts[parts.length - 1]);
                              } else if (key.startsWith('empty-')) {
                                // Pour les slots vides
                                return parseFloat(key.replace('empty-', ''));
                              }
                              return 0;
                            };
                            
                            return getPosition(a) - getPosition(b);
                          });
                        })()}
                      </div>
                    )}
                  </div>
                </CardBody>
              )}
              )}
            </Card>
          ) : (
            <p className="empty-stack-message">{t('noItemsStacked')}</p>
          )}

          {/* Card des produits de la première ligne */}
          {centralStack.find((line) => line?.line == 0 && line?.ProjProduct?.ref == "EMPTY")?.ProjSlots?.length > 0 && (
            <Card className="stacked-items-list" style={{ marginTop: 24, marginBottom: 16 }}>
              <CardBody className="stacked-item">
                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                  {t('accessories') || 'Produits de la première ligne'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16,  alignItems: 'flex-start', marginLeft: 16 }}>
                  {centralStack.find((line) => line?.line == 0 && line?.ProjProduct?.ref == "EMPTY")?.ProjSlots?.map((prod, idx) => (
                    <div className="accessories" key={prod.id || idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 48, gap: 12}}>
                      <div className="stacked-slot-element-label">{prod.ProjProduct?.lib || ''}</div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        
        
        <div className="sidebar-footer" id='sidebar-footer'>
          <button
            id="validateConfigButton"
            className="validate-button btn btn-primary"
            onClick={handleShowValidationPopup}
            disabled={isValidateButtonDisabled()}
          >
            {t('validate')}
          </button>
          
          {isValidateButtonDisabled() && (
            <UncontrolledTooltip
              className="slot-tooltip"
              innerClassName="error-tooltip validation-error-tooltip"
              transition={{ timeout: 300 }}
              placement="top"
              fade={false}
              target="sidebar-footer"
            >
              <div className="validation-errors">
                <strong>{t('cannotValidate')}:</strong>
                <div className="error-list">
                  {getValidateButtonTooltipMessage().split('\n').map((message, index) => (
                    <div key={index} className="error-item">
                      {message}
                    </div>
                  ))}
                </div>
              </div>
            </UncontrolledTooltip>
          )}
        </div>
      </div>
      
      {/* Message d'erreur */}
      {dragErrorMessage && (
        <div className="error-message">
          {dragErrorMessage}
        </div>
      )}
      
      {/* Modal de validation */}
      <RenderValidationModal
        showValidationPopup={showValidationPopup}
        handleCloseValidationPopup={handleCloseValidationPopup}
        handleConfirmValidation={handleConfirmValidation}
        loadingVerifyConfig={loadingVerifyConfig}
        centralStack={centralStack}
        elementSlots={elementSlots}
        renderElementSlot={renderElementSlot}
        renderElementOverlays={renderElementOverlays}
        transparentImage={transparentImage}
        handleGenerateImage={handleGenerateImage}
        isValidationInProgress={isValidationInProgress}
        setIsValidationInProgress={setIsValidationInProgress}
      />
      <IntensityModal isModalOpen={isIntensityModalOpen} setModalOpen={setIsIntensityModalOpen} config={configToSend} isConfig={true} />

      <RenderConfirmationPopup/>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className={loadingCreateQuote || loadingQuoteToPdf || loadingDoc || lastCreatedQuote ? "d-flex w-100 h-100 loadingHoverlay flex-column" : "d-none"}>
        <Spinner size={"lg"} />

        <span className="loading-text mt-3">{t('loadingQuote')}</span>
      </div>
    </div>
    
  );
}